<template>
  <donut-form ref="form" :method="props.program.id ? 'put' : 'post'" action="/shoutouts/settings" class="m-lx2">
    <input type="hidden" name="shoutouts_program[state]" value="active">
    <input type="hidden" name="from_setup" value="true">
    <!-- Emoji -->
    <div class="f-as">
      <div>
        <div class="t-lg bold">Set Your Emoji</div>
        <div v-emoji="emojiDesc" class="t-sm gray m-tx1 m-bx2" />
        <a
          v-if="reauthUrlForCustomEmoji && !upgradeRequired('emoji')"
          class="t-sm link"
          @click="openPermissionsModal"
        >Use a custom emoji from your Slack workspace</a>
        <points-slack-previews-channel-shoutout-message class="m-tx2" />
        <points-slack-previews-channel-shoutout-received-dm class="m-tx2" />
      </div>
      <donut-selector-emoji
        v-model="emojiSelection"
        form-name="shoutouts_program[emoji]"
        :categories="['foods', 'activity', 'nature', 'places', 'objects', 'symbols', 'flags', 'people', 'customSlack']"
        :paywall="emojiPaywall"
      />
      <input type="hidden" name="shoutouts_program[points_name]" :value="pointsName">
    </div>
    <!-- Channel -->
    <div class="m-tx4">
      <div class="t-lg bold">{{ isNewChannel ? 'Name' : 'Choose' }} Your Donut Channel</div>
      <div class="t-sm gray m-vx1">{{ channelsDesc }}</div>
      <donut-input-new-channel
        v-if="isNewChannel"
        v-model="newChannelName"
        :placeholder="$i18n.t('shoutouts.feature').toLowerCase()"
        @update-is-valid="(val) => newChannelNameIsValid = val"
      />
      <donut-form-input
        v-else
        :for="existingChannelOrChannels"
        name="channels"
      >
        <donut-selector-prefabs-channel v-model="existingChannelOrChannels" :is-multi="isMultiChannel" />
      </donut-form-input>
      <input type="hidden" name="shoutouts_program[channels][]" :value="channelSelection">
      <div v-if="!props.noAccessReason" class="t link c-p m-t" @click="changeType">{{ isNewChannel ? 'Use an existing channel' : 'Create new channel' }}</div>
      <div v-if="!restrictToChannels" class="t-sm gray m-t">
        <span>Donut will also detect {{ $i18n.t('shoutouts.shoutout', { count: 2 }) }} in any other Slack channel</span>
        <span> Donut is a member of — like #birthdays, #buddy-system, #cats, or #new-hires.</span>
      </div>
      <div class="f-ac m-t">
        <donut-input-checkbox
          v-model="restrictToChannels"
          form-name="shoutouts_program[restrict_to_notification_channels]"
          :label="restrictLabel"
        />
        <i v-tooltip="restrictTooltip" class="mi md-16 m-lx1 dark-gray">info</i>
      </div>
    </div>
    <!-- Submission -->
    <div class="f-ac f-je m-tx2">
      <a v-if="props.program.id && !props.noAccessReason" class="link m-rx2" href="/shoutouts/settings">More settings</a>
      <div class="f-v f-ae">
        <donut-button
          :class="{ disabled: !channelSelection, loading: state.formSubmitted }"
          :show-upgrade-badge="noAccessReason === 'upgrade_required'"
          @click="onTryItClick"
        >
          Try Shoutouts
        </donut-button>
        <div
          v-if="props.boostToggleDescription"
          class="m-tx1"
        >
          <donut-input-checkbox v-model="boostEnabled" form-name="shoutouts_program[boost_enabled]">
            <template #label>
              <div class="f-h f-ac">
                <div class="t dark-gray">Start with a boost</div>
                <i v-tooltip="boostTooltip" class="mi md-16 m-lx1 dark-gray">info</i>
              </div>
            </template>
          </donut-input-checkbox>
        </div>
      </div>
    </div>
  </donut-form>
</template>

<script setup>
  import { ref, computed, inject, onBeforeMount, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import useShoutoutsPointsAndEmojis from 'composables/shoutouts/points-and-emojis';

  const $i18n = inject('$i18n');
  const { state, commit, getters, dispatch } = useStore();

  const props = defineProps({
    reauthData: { type: Object, default: null },
    program: { type: Object, required: true },
    noAccessReason: { type: String, default: null },
    shoutoutsAdmins: { type: Array, default: null },
    boostToggleDescription: { type: String, default: null },
  });

  let boostTooltip = null;
  if (props.boostToggleDescription) {
    // eslint-disable-next-line no-restricted-syntax -- formatTextForSlack escapes HTML
    boostTooltip = { content: Donut.Formatter.formatTextForSlack(props.boostToggleDescription), html: true };
  }
  const boostEnabled = ref(true);

  const restrictToChannels = ref(!!props.program.restrict_to_notification_channels);
  const featureName = $i18n.t('shoutouts.feature');
  const restrictLabel = `Only use ${featureName} in selected channels`;
  const restrictTooltip = `By default, you can use ${featureName} in all channels Donut is in. By checking this box, you limit ${featureName} just to your selected channels, useful for testing and team- or function-specific recognition programs.`;

  // Paywalls
  const upgradeRequired = (feature) => getters.upgradeRequired(props.program.upgrade_data, feature);
  const emojiPaywall = computed(() => ({
      data: props.program.upgrade_data,
      feature: 'emoji',
      featureTitleText: 'customize the emoji used for points',
      source: 'shoutouts-setup-emoji-selector',
      programKey: 'shoutouts',
    }));

  // Slack Auth
  const reauthUrlForCustomEmoji = computed(() => state.reauthing.slackAuth?.custom_emoji?.path);
  const openPermissionsModal = () => {
    const source = 'Shoutouts Program Setup: Custom Emoji';
    dispatch('reauthing/openPermissionsModal', { contextName: 'custom_emoji', source, missingScope: 'emoji_read' });
  };

  // Emoji
  const pointsName = computed(() => state.pointsConfig.config.points_name);
  const emojiSelection = computed({
    get() { return state.pointsConfig.config.emoji; },
    set(emoji) {
      commit('pointsConfig/updateConfigKey', { key: 'emoji', value: emoji.id });
      if (!emoji.fromMount) commit('pointsConfig/updateConfigKey', { key: 'points_name', value: emoji.name });
    },
  });
  const { emojiDesc } = useShoutoutsPointsAndEmojis(pointsName);

  // Channel
  const newChannelName = ref('donut-shoutouts');
  const newChannelNameIsValid = ref(false);
  const existingChannelOrChannels = ref(null); // Might be an object or an array of objects
  const isNewChannel = ref(!props.noAccessReason);
  const isMultiChannel = computed(() => props.program.channels.length > 1);
  const changeType = () => { isNewChannel.value = !isNewChannel.value; };
  const channelSelection = computed(() => {
    if (isNewChannel.value && newChannelNameIsValid.value) return [newChannelName.value];
    if (!isNewChannel.value && existingChannelOrChannels.value) {
      if (isMultiChannel.value) return existingChannelOrChannels.value.map(c => c.id);
      return [existingChannelOrChannels.value.id];
    }
  });
  const channelsDesc = computed(() => {
    const verb = isNewChannel.value ? 'Create a new' : 'Choose a';
    return `${verb} Slack channel where your team will share appreciation. You can always change this later.`;
  });
  const initializeChannels = () => {
    // In the event that there is already a program and channels have already been selected
    const existingChannels = props.program.channels;
    if (existingChannels.length) {
      isNewChannel.value = false;
      if (existingChannels.length > 1) {
        existingChannelOrChannels.value = existingChannels;
      } else {
        existingChannelOrChannels.value = existingChannels[0];
      }
    }
  };

  // Form Submission
  const form = ref(null);
  const onTryItClick = () => {
    window.donut.track('Shoutouts Program - Clicked "Try It"', { noAccessReason: props.noAccessReason });
    if (props.noAccessReason === 'contact') {
      commit('updateModal', {
        visible: true,
        subcomponent: 'payment-modals-contact-to-get-started',
        data: { admins: props.shoutoutsAdmins, source: 'shoutouts-landing' },
      });
    } else if (props.noAccessReason === 'upgrade_required') {
      dispatch('openChannelProgramsPlanUpgradeModal', {
        trialAllowed: state.currentUser.onboarding_trial_allowed,
        data: {
          allowPlanSelection: false,
          defaultPremiumPlan: true,
          featureTitleText: `use ${$i18n.t('shoutouts.feature')}`,
          featureInfo: {
            programKey: 'shoutouts',
          },
          source: 'shoutouts-landing',
          redirectLocation: '/shoutouts',
        },
      });
    } else {
      window.Intercom('trackEvent', 'Shoutouts: Clicked "Try It"');
      form.value.submit();
    }
  };

  // Lifecycle Hooks
  onBeforeMount(() => {
    commit('update', { module: 'reauthing', key: 'slackAuth', value: props.reauthData });
  });
  onMounted(() => {
    initializeChannels();
  });
</script>
